// src/modules/app-views/services/pages/Document/tabs/RecordTab/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useMemo } from 'react';
import { Card, Col, Row, Form } from 'antd';
import { ROW_GUTTER } from 'constants/ThemeConstant';
import translateX from 'utils/translateX';
import SimpleInput from 'components/SimpleComponents/SimpleInput';
import SimpleSelect from 'components/SimpleComponents/SimpleSelect';
import SimpleCascader from 'components/SimpleComponents/SimpleCascader';
import SimpleCurrency from 'components/SimpleComponents/SimpleCurrency';
import SimpleLoadingArea from 'components/SimpleComponents/SimpleLoadingArea';
import useZustand from 'hooks/useZustand';
import { useDocumentContext } from 'components/core/DocumentProvider';
import { useSimpleFormContext } from 'components/SimpleComponents/SimpleFormProvider';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const ServicesRecordTab = () => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const storedParameters = useZustand('app', 'parameters');
  const storedModule = useZustand('module', 'config');
  const { data, loading, _id: documentId } = useDocumentContext() || {};
  const { form } = useSimpleFormContext() || {};

  // Watch current unitUid value to show removed unit if service is using it
  const currentUnitUid = Form.useWatch('unitUid', form);

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const unitsOptions = useMemo(
    () => buildUnitsOptions(storedParameters, storedModule?.id, documentId, currentUnitUid),
    [storedParameters, storedModule?.id, documentId, currentUnitUid]
  );

  const categoriesOptions = useMemo(
    () => buildCategoriesOptions(storedParameters, storedModule?.id),
    [storedParameters, storedModule?.id]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className="container" style={{ position: 'relative', minHeight: 120 }}>
      <SimpleLoadingArea name="serviceRecord" isFetching={loading} data={data} />

      {/* SERVICE DATA CARD */}
      <Card title={<div className="text-muted">{translateX('service_data')}</div>}>
        <Row gutter={ROW_GUTTER}>
          <Col xs={24} sm={6} lg={5}>
            <SimpleInput name="sku" tooltip="auto_generated" />
          </Col>

          <Col xs={24} sm={18} lg={9}>
            <SimpleInput name="name" label="name" required />
          </Col>

          <Col xs={24} sm={6} lg={4}>
            <SimpleSelect
              name="unitUid"
              label="unit"
              allowClear
              showSearch
              options={unitsOptions}
              showAddButton={{ modal: 'parameters', tab: 'units' }}
            />
          </Col>

          <Col xs={24} sm={8} lg={6}>
            <SimpleCurrency
              name="price"
              label="price"
              currencies={[{ value: 'BRL', label: 'R$', text: 'Real Brasileiro' }]}
            />
          </Col>
        </Row>

        <Row gutter={ROW_GUTTER}>
          <Col xs={24} sm={12}>
            <SimpleCascader
              name="categoryUids"
              label="category"
              options={categoriesOptions}
              placeholder="select_category"
              showAddButton={{ modal: 'parameters', tab: 'categories' }}
            />
          </Col>

          <Col xs={24} sm={12}>
            <SimpleInput name="description" label="description" type="textarea" rows={2} />
          </Col>
        </Row>
      </Card>
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default ServicesRecordTab;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function buildUnitsOptions(storedParameters, moduleId, documentId, currentUnitUid) {
  const unitsArray = storedParameters?.modules?.[moduleId]?.units;

  if (!unitsArray || !Array.isArray(unitsArray)) return [];

  const isNewDocument = !documentId || documentId === 'add';

  const filteredUnits = unitsArray.filter(item => {
    if (!item.removed) return true;
    if (isNewDocument) return false;
    return item.uid === currentUnitUid;
  });

  return filteredUnits.map(item => {
    const name = item.name || '';
    const description = item.description || '';
    const displayLabel = description ? `${name} - ${description}` : name;

    return {
      value: item.uid,
      text: displayLabel,
      label: name,
      disabled: false,
    };
  });
}

function buildCategoriesOptions(storedParameters, moduleId) {
  const rawCategories = storedParameters?.modules?.[moduleId]?.categories;

  if (!rawCategories || !Array.isArray(rawCategories) || rawCategories.length === 0) {
    return [];
  }

  // Transform categories to Cascader format (value, label, children)
  return transformCategoriesToCascader(rawCategories);
}

function transformCategoriesToCascader(categories) {
  if (!categories || !Array.isArray(categories)) return [];

  return categories.map(cat => ({
    value: cat.uid,
    label: cat.label,
    children: cat.children?.length > 0 ? transformCategoriesToCascader(cat.children) : undefined,
  }));
}
